import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { User, Mail, Phone, MessageSquare, Send, MapPin, Clock, Globe } from "lucide-react";
import Navbar from "./Navbar";

export default function ContactPage() {
  const formRef = useRef();

  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSending(true);
    setSuccess("");
    setError("");

    try {
      await emailjs.sendForm(
        "service_ecfbcpi",
        "template_g4gf1p7",
        formRef.current,
        {
          publicKey: "1KCrv-bR7fKaqZz-D",
        }
      );

      setSuccess(
        "Thank you! Your message has been sent successfully."
      );

      // Clear form
      formRef.current.reset();

    } catch (err) {
      console.error("EmailJS Error:", err);

      setError(
        "Unable to send your message. Please try again."
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="grid lg:grid-cols-2 gap-10">

        {/* Contact Information */}
        <div className="bg-[#F4F8FF] rounded-3xl p-6 md:p-8">

          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Contact Information
          </h3>

          <div className="space-y-6">

            {/* Address */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                <MapPin
                  size={22}
                  className="text-[#2864E8]"
                />
              </div>

              <div>
                <h4 className="font-semibold text-gray-900">
                  Address
                </h4>

                <p className="text-gray-600 mt-1 leading-relaxed">
                  No.123, Anna Nagar, Chennai - 600040
                </p>
              </div>
            </div>


            {/* Phone */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                <Phone
                  size={22}
                  className="text-[#2864E8]"
                />
              </div>

              <div>
                <h4 className="font-semibold text-gray-900">
                  Phone
                </h4>

                <a
                  href="tel:+919876543210"
                  className="text-gray-600 hover:text-[#2864E8] mt-1 block transition"
                >
                  +91 9876543210
                </a>
              </div>
            </div>


            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail
                  size={22}
                  className="text-[#2864E8]"
                />
              </div>

              <div>
                <h4 className="font-semibold text-gray-900">
                  Email
                </h4>

                <a
                  href="mailto:info@msmtechnologies.in"
                  className="text-gray-600 hover:text-[#2864E8] mt-1 block transition"
                >
                  info@msmtechnologies.in
                </a>
              </div>
            </div>


            {/* Working Hours */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock
                  size={22}
                  className="text-[#2864E8]"
                />
              </div>

              <div>
                <h4 className="font-semibold text-gray-900">
                  Working Hours
                </h4>

                <p className="text-gray-600 mt-1 leading-relaxed">
                  Monday - Saturday
                  <br />
                  9:00 AM - 7:00 PM
                </p>
              </div>
            </div>


            {/* Website */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                <Globe
                  size={22}
                  className="text-[#2864E8]"
                />
              </div>

              <div>
                <h4 className="font-semibold text-gray-900">
                  Website
                </h4>

                <a
                  href="https://www.msmtechnologies.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-[#2864E8] mt-1 block transition"
                >
                  www.msmtechnologies.in
                </a>
              </div>
            </div>

          </div>

        </div>


        <section className="py-5 px-6 bg-white">

          <div className="max-w-6xl mx-auto">

            {/* Heading */}
            <h2 className="text-3xl font-bold text-gray-800 mb-8">
              Send us a Message
            </h2>

            {/* Contact Form */}

            <div className="max-w-3xl mx-auto">

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="space-y-5"
              >

                {/* Name */}

                <div>

                  <label className="block text-sm font-medium mb-2">
                    Full Name
                  </label>

                  <div className="relative">

                    <User
                      size={20}
                      className="absolute left-4 top-3.5 text-gray-400"
                    />

                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your name"
                      required
                      className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 outline-none focus:border-[#2864E8] focus:ring-2 focus:ring-blue-100 transition"
                    />

                  </div>

                </div>


                {/* Email */}

                <div>

                  <label className="block text-sm font-medium mb-2">
                    Email Address
                  </label>

                  <div className="relative">

                    <Mail
                      size={20}
                      className="absolute left-4 top-3.5 text-gray-400"
                    />

                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      required
                      className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 outline-none focus:border-[#2864E8] focus:ring-2 focus:ring-blue-100 transition"
                    />

                  </div>

                </div>


                {/* Mobile */}

                <div>

                  <label className="block text-sm font-medium mb-2">
                    Mobile Number
                  </label>

                  <div className="relative">

                    <Phone
                      size={20}
                      className="absolute left-4 top-3.5 text-gray-400"
                    />

                    <input
                      type="tel"
                      name="mobile"
                      placeholder="Enter your mobile number"
                      required
                      className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 outline-none focus:border-[#2864E8] focus:ring-2 focus:ring-blue-100 transition"
                    />

                  </div>

                </div>


                {/* Subject */}

                <div>

                  <label className="block text-sm font-medium mb-2">
                    Subject
                  </label>

                  <div className="relative">

                    <MessageSquare
                      size={20}
                      className="absolute left-4 top-3.5 text-gray-400"
                    />

                    <input
                      type="text"
                      name="subject"
                      placeholder="Enter subject"
                      required
                      className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 outline-none focus:border-[#2864E8] focus:ring-2 focus:ring-blue-100 transition"
                    />

                  </div>

                </div>


                {/* Message */}

                <div>

                  <label className="block text-sm font-medium mb-2">
                    Message
                  </label>

                  <textarea
                    name="message"
                    rows="5"
                    placeholder="Write your message..."
                    required
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none resize-none focus:border-[#2864E8] focus:ring-2 focus:ring-blue-100 transition"
                  />

                </div>


                {/* Success */}

                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl">
                    {success}
                  </div>
                )}


                {/* Error */}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl">
                    {error}
                  </div>
                )}


                {/* Submit */}

                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full md:w-auto flex items-center justify-center gap-2 bg-[#2864E8] hover:bg-[#1f55cc] text-white px-8 py-3.5 rounded-xl font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                >

                  {isSending ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}

                </button>

              </form>

            </div>

          </div>

        </section>





      </div>



    </>
  );
}